import React, { useState, useContext } from 'react'
import { Formik } from 'formik'
import { Form } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@material-ui/core/useMediaQuery'

import BaseContainer from '../common/BaseContainer'
import ControlHeader from '../common/ControlHeader'
import PrimaryButton from '../common/buttons/PrimaryButton'
import Loading from '../common/icons/Loading'
import generateFormikInputs from '../../utils/generateFormikInputs'
import fetch from '../../utils/fetcher'
import { ModalManagerContext } from '../ModalsManager'
import ServerErrorMessage from '../common/ServerErrorMessage'
import Error from '../common/icons/Error'
import FocusOnFirstError from '../common/Formik/FocusOnFirstError'
import { SuccessMessage, ErrorMessage, useVisible } from '../common/AlertManager'
import {
  baseUrl,
  formToRequestData,
  validationSchema,
  requestToFormData,
  baseInputsData,
  bandwidthInputsData,
  getAppCheckboxesData,
  appCategoriesCheckboxesData,
  webPagesCategoriesCheckboxesData,
  withPasswordCheckbox,
  withPasswordBaseInputsData,
  withPasswordBandwidthInputsData,
  withPasswordAppCheckboxesData,
  withPasswordAppCategoriesCheckboxesData,
  withPasswordWebPagesCategoriesCheckboxesData,
} from './config'

import useStyles from './styles'
import useFetch from '../../utils/useFetch'


const Network = () => {
  const { t } = useTranslation(['translation', 'COMMON'])
  const openModal = useContext(ModalManagerContext)
  const [alertError, setAlertError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const { visible: isErrorVisible, open: openError } = useVisible(false)
  const { visible: isSuccessMessageVisible, open: openSuccessMessage } = useVisible(false)
  const isMobile = useMediaQuery('(max-width:900px)')
  const { render } = useFetch(`${process.env.REACT_APP_API_URL}/network`, openModal)

  // const render = (foo) => foo({
  //   guest: {
  //     ssid: 'ssid',
  //     limitUp: { value: '1024', label: '1 Mbps' },
  //     limitDown: { value: '1024', label: '1 Mbps' },
  //     rules: [
  //       { name: 'facebook' }
  //     ]
  //   },
  //   employee: {
  //     ssid: 1,
  //     limitUp: { value: '1024', label: '1 Mbps' },
  //     limitDown: { value: '1024', label: '1 Mbps' },
  //     rules: [
  //       { name: 'facebook'}
  //     ]
  //   }
  // });

  const classes = useStyles()
  const onSubmit = (values, { setSubmitting }) => {
    const url = baseUrl
    setAlertError(null)
    setSuccessMessage(null)
    fetch(url, formToRequestData(values))
      .then(res => res.json())
      .then((res) => {
        const { employee, guest } = res
        if (employee?.status && guest.status) {
          setSuccessMessage(t('COMMON.SUCCESSFUL_UPDATE'))
          openSuccessMessage()
        } else {
          setAlertError({
            errorCode: employee?.status === false ? 'PASSWORD_NETWORK_ERROR' : 'NETWORK_ERROR',
            resource: 'NETWORK',
          })
          openError()
        }
      })
      .catch(e => {
        setAlertError(e)
        openError()
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <BaseContainer>
      <ControlHeader
        title={t('NETWORK.TITLE')}
        showTabs
        activeTab="network"
      />
      {render(data => {
        try {
          const formData = requestToFormData(data)
          const isFacebookEnabled = !data.facebookLogin
          const appCheckboxesData = getAppCheckboxesData(isFacebookEnabled)
          return (
            <Formik
              initialValues={formData}
              onSubmit={onSubmit}
              validationSchema={validationSchema}
              validateOnChange={false}
            >
              {({
                handleSubmit,
                isSubmitting,
                ...formikProps
              }) => {
                const filtersMarginTop = !isMobile && formikProps.values.withPassword ? formikProps.isValid ? '150px' : '250px' : 0
                return (
                  <Form onSubmit={handleSubmit}>
                    <div className={classes.inputsGrid}>
                      <div>
                        <h3 className={classes.mainHeader}>{t('NETWORK.CLIENT_WIFI')}</h3>
                        {generateFormikInputs(baseInputsData, formikProps, false, isSubmitting)}
                        <h5 className={classes.subsectionHeader}>{t('NETWORK.DEVICE_BANDWIDTH')}</h5>
                        {generateFormikInputs(bandwidthInputsData, formikProps, false, isSubmitting)}
                      </div>
                      <div>
                        <h3 className={classes.mainHeader}>{t('NETWORK.EMPLOYEES_WIFI')}</h3>
                        {generateFormikInputs(withPasswordCheckbox, formikProps, false, isSubmitting)}
                        {formikProps.values.withPassword &&
                          <>
                            {generateFormikInputs(
                              withPasswordBaseInputsData, formikProps, false, isSubmitting,
                            )}
                            <h5 className={classes.subsectionHeader}>{t('NETWORK.DEVICE_BANDWIDTH')}</h5>
                            {generateFormikInputs(
                              withPasswordBandwidthInputsData, formikProps, false, isSubmitting,
                            )}
                          </>
                        }
                      </div>
                      <div style={{ marginTop: filtersMarginTop }}>
                        <h4 className={classes.header}>{t('NETWORK.CONTENT_FILTERS')}</h4>
                        <h5 className={classes.subheader}>{t('NETWORK.APPLICATIONS')}</h5>
                        {generateFormikInputs(appCheckboxesData, formikProps, false, isSubmitting)}
                        <h5 className={classes.subheader}>{t('NETWORK.APP_CATEGORIES')}</h5>
                        {generateFormikInputs(
                          appCategoriesCheckboxesData, formikProps, false, isSubmitting,
                        )}
                        <h5 className={classes.subheader}>{t('NETWORK.WEB_CATEGORIES')}</h5>
                        {generateFormikInputs(
                          webPagesCategoriesCheckboxesData, formikProps, false, isSubmitting,
                        )}
                      </div>
                      {formikProps.values.withPassword && (
                        <div style={{ marginTop: filtersMarginTop }}>
                          <h4 className={classes.header}>{t('NETWORK.CONTENT_FILTERS_EMPLOYEES')}</h4>
                          <h5 className={classes.subheader}>{t('NETWORK.APPLICATIONS')}</h5>
                          {generateFormikInputs(
                            withPasswordAppCheckboxesData, formikProps, false, isSubmitting,
                          )}
                          <h5 className={classes.subheader}>{t('NETWORK.APP_CATEGORIES')}</h5>
                          {generateFormikInputs(
                            withPasswordAppCategoriesCheckboxesData, formikProps, false, isSubmitting,
                          )}
                          <h5 className={classes.subheader}>{t('NETWORK.WEB_CATEGORIES')}</h5>
                          {generateFormikInputs(
                            withPasswordWebPagesCategoriesCheckboxesData, formikProps, false, isSubmitting,
                          )}
                        </div>
                      )}
                    </div>

                    <ErrorMessage error={alertError} visible={isErrorVisible} />
                    {successMessage && <SuccessMessage message={successMessage} visible={isSuccessMessageVisible} /> }
                    {isSubmitting && <Loading />}

                    <PrimaryButton type="submit" className={classes.button} disabled={isSubmitting}>
                      {t('NETWORK.SAVE_BUTTON')}
                    </PrimaryButton>

                    <FocusOnFirstError />
                  </Form>
                )
              }}
            </Formik>
          )
        } catch (err) {
          openModal(ServerErrorMessage);
          return <Error />;
        }
      }
      )}
    </BaseContainer>
  )
}


export default Network
